import { ServiceVariation } from "@/services/backend/omni/servicevariation/types/service-variation";
import { v4 } from "uuid";
import t from "@/lang/lang";
import { Position, PositionSubscriptionInterval } from "./position";
import { PositionKind } from "./position-kind";
import { PositionUnit } from "./position-unit";
import { PositionCategory } from "./position-category";
import { AddSubPositionRequest } from "./position-requests";
import { PriceUsed } from "./price-used";

/** Mirrors `positions.Option`, backend/core/omni/domain/positions/option.go. */
export type PositionBaseData = {
  label: string;
  kind: PositionKind;
  serviceVariation: ServiceVariation;
  text: string;
  textShort: string;
  productNumber: string;
  category: PositionCategory;
  unit: PositionUnit | "";
  unitPrice: number;
  count: number;
  hours: number;
  hoursRate: number;
  cycle: number;
  subscriptionInterval: PositionSubscriptionInterval;
  priceUsed: PriceUsed;
  dataSheetIds: string[] | null;
  subPositions: PositionBaseDataSubPosition[] | null;
  availableIn: OptionAvailability[];
  seed: boolean;
};

/** Mirrors `positions.OptionSubPosition`, backend/core/omni/domain/positions/option.go. */
export type PositionBaseDataSubPosition = {
  kind: PositionKind;
  text: string;
  textShort: string;
  productNumber: string;
  category: PositionCategory;
  unit: PositionUnit | "";
  unitPrice: number;
  count: number;
  hours: number;
  hoursRate: number;
  cycle: number;
  subscriptionInterval: PositionSubscriptionInterval;
  priceUsed: PriceUsed;
};

/** newPositionBaseDataSubPosition returns an empty sub-position of kind. */
export function newPositionBaseDataSubPosition(
  kind: PositionKind,
): PositionBaseDataSubPosition {
  return {
    kind,
    text: "",
    textShort: "",
    productNumber: "",
    category: PositionCategory.Zero,
    unit: "",
    unitPrice: 0,
    count: 0,
    hours: 0,
    hoursRate: 0,
    cycle: 0,
    subscriptionInterval: PositionSubscriptionInterval.None,
    priceUsed: PriceUsed.Zero,
  };
}

/** Mirrors `positions.Availability`, backend/core/omni/domain/positions/availability.go. */
export const OptionAvailability = {
  ZeroGrouping: "zero-grouping",
  Group: "group",
  Area: "area",
  GroupArea: "group-area",
} as const;

export type OptionAvailability =
  (typeof OptionAvailability)[keyof typeof OptionAvailability];

const allAvailabilities = Object.values(OptionAvailability);

/**
 * groupingAvailability returns where a position of the grouping is
 * added, see `positions.AvailabilityOf`,
 * backend/core/omni/domain/positions/availability.go.
 */
export function groupingAvailability(grouping: {
  group?: string;
  area?: string;
}): OptionAvailability {
  const group = (grouping.group ?? "") !== "";
  const area = (grouping.area ?? "") !== "";
  if (group && area) {
    return OptionAvailability.GroupArea;
  }
  if (group) {
    return OptionAvailability.Group;
  }
  if (area) {
    return OptionAvailability.Area;
  }
  return OptionAvailability.ZeroGrouping;
}

/** PositionOptions are the options offered at each place of a document. */
export type PositionOptions = Record<OptionAvailability, PositionBaseData[]>;

/**
 * positionOptionsByAvailability returns the options offered at each
 * place, by their availableIn.
 */
export function positionOptionsByAvailability(
  options: PositionBaseData[],
): PositionOptions {
  const availableIn = (a: OptionAvailability) =>
    options.filter((o) => o.availableIn.includes(a));
  return {
    [OptionAvailability.ZeroGrouping]: availableIn(
      OptionAvailability.ZeroGrouping,
    ),
    [OptionAvailability.Group]: availableIn(OptionAvailability.Group),
    [OptionAvailability.Area]: availableIn(OptionAvailability.Area),
    [OptionAvailability.GroupArea]: availableIn(OptionAvailability.GroupArea),
  };
}

export function newPositionBaseData(
  kind: PositionKind,
  label: string,
): PositionBaseData {
  return {
    label,
    kind,
    serviceVariation: ServiceVariation.Zero,
    text: "",
    textShort: "",
    productNumber: "",
    category: PositionCategory.Zero,
    unit: "",
    unitPrice: 0,
    count: 0,
    hours: 0,
    hoursRate: 0,
    cycle: 0,
    subscriptionInterval: PositionSubscriptionInterval.None,
    priceUsed: PriceUsed.Zero,
    dataSheetIds: [],
    subPositions: [],
    availableIn: [OptionAvailability.ZeroGrouping],
    seed: false,
  };
}

/**
 * newSubPositions returns the sub-positions of the request adding a
 * position of option, each with a new id and its values.
 */
export function newSubPositions(
  option: PositionBaseData,
): AddSubPositionRequest[] {
  return (option.subPositions ?? []).map((sub) => ({
    positionId: v4(),
    ...sub,
  }));
}

const intervalDays: Record<PositionSubscriptionInterval, number> = {
  [PositionSubscriptionInterval.None]: 0,
  [PositionSubscriptionInterval.Day]: 1,
  [PositionSubscriptionInterval.Week]: 7,
  [PositionSubscriptionInterval.Month]: 365 / 12,
  [PositionSubscriptionInterval.Quarter]: (365 / 12) * 3,
  [PositionSubscriptionInterval.Year]: 365,
};

/**
 * optionSubscriptionPrice returns the subscription price a position
 * added from option starts with, see `Position.calculate` and
 * `Position.calculateSubscriptionPrice`,
 * backend/core/omni/domain/positions/position.go. It is undefined
 * without a cycle or an interval, and for the kinds whose price depends
 * on the other positions of the document or that have none.
 */
export function optionSubscriptionPrice(
  option: Pick<
    PositionBaseData,
    "kind" | "count" | "unitPrice" | "cycle" | "subscriptionInterval"
  >,
): number | undefined {
  const days = intervalDays[option.subscriptionInterval] ?? 0;
  if (option.cycle <= 0 || days === 0) {
    return undefined;
  }
  switch (option.kind) {
    case PositionKind.Default:
    case PositionKind.Dynamic:
    case PositionKind.PriceModifierAbsolute:
      return ((option.count * option.unitPrice) / (option.cycle * 7)) * days;
    default:
      return undefined;
  }
}

/**
 * defaultPositionBaseData are offered to add positions while a company
 * has no positions base data set as default for the service kind.
 */
export const defaultPositionBaseData: PositionBaseData[] = [
  {
    ...newPositionBaseData(PositionKind.Default, t("Freitext")),
    availableIn: allAvailabilities,
  },
  {
    ...newPositionBaseData(PositionKind.Text, t("Text (ohne Preis)")),
    availableIn: allAvailabilities,
  },
];

/**
 * offerablePositionBaseData returns the options that may be added to
 * positions: a document has at most one total, so it is only offered
 * while positions has none.
 */
export function offerablePositionBaseData(
  positions: Position[],
  options: PositionOptions,
): PositionOptions {
  const hasTotal = positions.some((p) => p.kind === PositionKind.Total);
  if (!hasTotal) {
    return options;
  }
  const withoutTotal = (a: OptionAvailability) =>
    options[a].filter((o) => o.kind !== PositionKind.Total);
  return {
    [OptionAvailability.ZeroGrouping]: withoutTotal(
      OptionAvailability.ZeroGrouping,
    ),
    [OptionAvailability.Group]: withoutTotal(OptionAvailability.Group),
    [OptionAvailability.Area]: withoutTotal(OptionAvailability.Area),
    [OptionAvailability.GroupArea]: withoutTotal(OptionAvailability.GroupArea),
  };
}
